import React, {
  createContext,
  ReactNode,
  useReducer,
  useEffect,
  useContext,
  useState,
} from 'react';
import {
  VFGlobalContext,
  VisitsGlobalContext,
} from '@mummssoftware/global-context';
import moment from 'moment-timezone';
import { getRoleGroups } from '@mummssoftware/utils/frequencies/frequency-utils';
import { fetchPimreaderVisitsForPatient } from '@mummssoftware/utils/pimReader';
import { AppContext } from '../../context';
import {
  computeCalendarViewData,
  fetchCarePlanFrequencies,
  toDateParam,
  CalendarDates,
  CalendarViewData,
} from '../data';
import {
  SET_CALENDAR_DATES,
  SET_CARE_PLAN_FREQUENCIES_FOR_RANGE,
  SET_CALENDAR_VIEW_DATA,
} from './constants';

type ScheduledVisitsContextValue = {
  calendarDates: CalendarDates | null;
  calendarViewData: CalendarViewData | null;
  carePlanFrequenciesForRange: mumms.patientCarePlanProblemFrequency[] | null;
  /** Role groups in use: the VF global list, or the standalone fetch (OP-33263). */
  roleGroups: mumms.RoleGroup[] | null;
  isCalendarLoading: boolean;
  dispatch: React.Dispatch<{ type: string; payload?: unknown }>;
};

type ScheduledVisitsContextState = {
  calendarDates: CalendarDates | null;
  calendarViewData: CalendarViewData | null;
  carePlanFrequenciesForRange: mumms.patientCarePlanProblemFrequency[] | null;
};

const initialState: ScheduledVisitsContextState = {
  calendarDates: null,
  calendarViewData: null,
  carePlanFrequenciesForRange: null,
};

const scheduledVisitsContextReducer = (
  state: ScheduledVisitsContextState,
  action: { type: string; payload?: unknown },
): ScheduledVisitsContextState => {
  switch (action.type) {
    case SET_CALENDAR_DATES:
      return {
        ...state,
        calendarDates: action.payload as CalendarDates,
        carePlanFrequenciesForRange: null,
        calendarViewData: null,
      };
    case SET_CARE_PLAN_FREQUENCIES_FOR_RANGE:
      return {
        ...state,
        carePlanFrequenciesForRange: action.payload as mumms.patientCarePlanProblemFrequency[],
        calendarViewData: null,
      };
    case SET_CALENDAR_VIEW_DATA:
      return {
        ...state,
        calendarViewData: action.payload as CalendarViewData,
      };
    default:
      return state;
  }
};

const defaultContext: ScheduledVisitsContextValue = {
  calendarDates: null,
  calendarViewData: null,
  carePlanFrequenciesForRange: null,
  roleGroups: null,
  isCalendarLoading: true,
  dispatch: () => undefined,
};

export const ScheduledVisitsContext = createContext<
  ScheduledVisitsContextValue
>(defaultContext);

type ScheduledVisitsContextProviderProps = {
  children: ReactNode;
};

export const ScheduledVisitsContextProvider = ({
  children,
}: ScheduledVisitsContextProviderProps) => {
  const [state, dispatch] = useReducer(
    scheduledVisitsContextReducer,
    initialState,
  );
  const {
    currentUser,
    patient,
    agency,
    scheduler,
    keycloak,
    standalone,
  } = useContext(AppContext);
  const { roleGroupsGlobal } = useContext(VFGlobalContext);
  const { patientVisitHistory: globalVisitHistory } = useContext(
    VisitsGlobalContext,
  );
  const {
    calendarDates,
    calendarViewData,
    carePlanFrequenciesForRange,
  } = state;

  // OP-33263: standalone (cross patient drill-down) — no CPVF or Visit Log
  // widget fills the global contexts, so load role groups and the patient's
  // visits for the calendar range here instead.
  const [standaloneRoleGroups, setStandaloneRoleGroups] = useState<
    mumms.RoleGroup[] | null
  >(null);
  const [standaloneVisits, setStandaloneVisits] = useState<
    mumms.pimreaderVisit[] | null
  >(null);
  const roleGroups = roleGroupsGlobal ?? standaloneRoleGroups;
  const patientVisitHistory = standalone
    ? standaloneVisits
    : globalVisitHistory;

  useEffect(() => {
    if (!standalone || roleGroupsGlobal || !keycloak || !agency) {
      return undefined;
    }
    let cancelled = false;
    getRoleGroups(keycloak, agency).then((groups) => {
      if (!cancelled) setStandaloneRoleGroups(groups || []);
    });
    return () => {
      cancelled = true;
    };
  }, [standalone, roleGroupsGlobal, keycloak, agency]);

  useEffect(() => {
    if (!standalone || !calendarDates || !keycloak || !agency || !patient) {
      return undefined;
    }
    let cancelled = false;
    setStandaloneVisits(null);
    // Two months back so periods that started before the view still score.
    const from = moment(toDateParam(calendarDates[0]), 'YYYY-MM-DD')
      .subtract(62, 'days')
      .toDate();
    const to = moment(toDateParam(calendarDates[1]), 'YYYY-MM-DD').toDate();
    fetchPimreaderVisitsForPatient(
      keycloak,
      agency,
      patient,
      null,
      from,
      to,
      null,
      null,
      null,
      null,
    ).then((visits) => {
      if (cancelled) return;
      setStandaloneVisits(
        Array.isArray(visits) ? (visits as mumms.pimreaderVisit[]) : [],
      );
    });
    return () => {
      cancelled = true;
    };
  }, [standalone, calendarDates, keycloak, agency, patient]);

  useEffect(() => {
    if (!calendarDates || !patient || !currentUser || !scheduler || !agency) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const frequencies = await fetchCarePlanFrequencies({
        scheduler,
        agency,
        siteId: patient.siteId?.toString() || '',
        patientNumber: patient.patientNumber,
        fromDate: toDateParam(calendarDates[0]),
        toDate: toDateParam(calendarDates[1]),
      });

      if (cancelled) {
        return;
      }

      dispatch({
        type: SET_CARE_PLAN_FREQUENCIES_FOR_RANGE,
        payload: frequencies,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [calendarDates, patient, currentUser, agency, scheduler]);

  useEffect(() => {
    if (
      !calendarDates ||
      !carePlanFrequenciesForRange ||
      !roleGroups ||
      patientVisitHistory === null ||
      !currentUser
    ) {
      return;
    }

    dispatch({
      type: SET_CALENDAR_VIEW_DATA,
      payload: computeCalendarViewData({
        calendarDates,
        carePlanFrequencies: carePlanFrequenciesForRange,
        visits: patientVisitHistory,
        roleGroups,
        startOfWeek: currentUser.startOfWeek ?? null,
        siteTimezone: currentUser.siteTimezone || moment.tz.guess(),
      }),
    });
  }, [
    calendarDates,
    carePlanFrequenciesForRange,
    roleGroups,
    patientVisitHistory,
    currentUser?.startOfWeek,
    currentUser?.siteTimezone,
  ]);

  const isCalendarLoading =
    !currentUser ||
    !patient ||
    roleGroups === null ||
    patientVisitHistory === null ||
    !calendarDates ||
    carePlanFrequenciesForRange === null ||
    calendarViewData === null;

  return (
    <ScheduledVisitsContext.Provider
      value={{
        calendarDates,
        calendarViewData,
        carePlanFrequenciesForRange,
        roleGroups,
        isCalendarLoading,
        dispatch,
      }}
    >
      {children}
    </ScheduledVisitsContext.Provider>
  );
};

export default ScheduledVisitsContextProvider;
